<template>
  <div class="header-bazar-voice">
    <div class="header-content">
      <div class="col col-1">
        <div class="line">
          <p class="big">{{ community.recommendation }}%</p>
          <p
            v-if="community.communityType === 'jeu'"
            class="small"
          >
            des joueurs recommandent ce jeu
          </p>
          <p
            v-if="community.communityType === 'console'"
            class="small"
          >
            des joueurs recommandent cette console
          </p>
          <p
            v-if="community.communityType === 'service'"
            class="small"
          >
            des joueurs recommandent ce service
          </p>
        </div>
        <div class="line">
          <p class="big">{{ numberToString(community.totalScore) }}</p>
          <p class="small-bis">/5</p>
          <ScoreResult
            :score="community.totalScore ?? 0"
            class="score"
          />
          <NuxtLink
            :to="`/communautes/${community.slug}/avis-joueurs`"
            class="link"
          >
            {{ community.total_review }} avis
          </NuxtLink>
        </div>
      </div>
      <div class="col col-2">
        <div
          v-for="score in community.scores"
          :key="score.score"
          class="line"
        >
          <ScoreResult :score="score.score" />
          <div class="gauge">
            <div
              class="innerGauge"
              :style="{ width: `${score.percent}%` }"
            />
          </div>
          <div class="percent">{{ score.percent }}%</div>
        </div>
      </div>
      <div class="col col-3">
        <div
          v-for="category in community.categories"
          :key="category.title"
          class="category"
        >
          <div class="text">
            <p class="title">{{ category.title }}</p>
            <p class="score">{{ numberToString(category.score) }}</p>
          </div>
          <div class="gauge">
            <div
              class="innerGauge"
              :style="{ width: `${(category.score / 5) * 100}%` }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CommunityStats } from '~/types'

defineProps<{
  community: CommunityStats
}>()
</script>

<style lang="scss" scoped>
.header-bazar-voice {
  position: relative;
  color: $white;
  background: $darkBackground;

  .header-content {
    display: flex;
    justify-content: space-between;
    max-width: 200rem;
    margin-right: auto;
    margin-left: auto;
    padding: 7.7rem 8rem 11rem;
  }

  .col {
    display: flex;
    flex-direction: column;
    flex-basis: 33.33%;
    justify-content: center;
    align-items: center;
    border-right: 1px solid #d1d1d1;

    &.col-3 {
      justify-content: flex-start;
      border-right: none;

      .category {
        width: 100%;
        margin-bottom: 1.2rem;
        padding-left: 10rem;
      }

      .gauge {
        width: 100%;
      }

      .text {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.4rem;

        .title {
          font-size: 1.4rem;
          line-height: 1.8rem;
        }

        .score {
          font-size: 2.6rem;
          line-height: 3.4rem;
        }
      }
    }

    &.col-1 {
      align-items: flex-start;
      padding-right: 8rem;

      .line {
        justify-content: flex-start;
        align-items: flex-end;
        width: 100%;
        padding-bottom: 1.5rem;
        border-bottom: 1px solid rgba($white, 0.2);
      }

      .big {
        font-size: 6rem;
        font-weight: 500;
        line-height: 7.9rem;
      }

      .small {
        margin-left: 1.8rem;
        padding-bottom: 1.2rem;
        font-size: 1.4rem;

        // line-height: 2.8rem;
        line-height: 1.3;
      }

      .small-bis {
        padding-bottom: 1.2rem;
        font-size: 2rem;
        font-weight: 500;
        line-height: 2.7rem;
      }

      .score {
        flex-grow: 1;
        margin-left: 3rem;
        padding-bottom: 1.2rem;
      }

      .link {
        margin-bottom: 1.2rem;
        margin-left: 1.8rem;
        color: $white;
        font-size: 1.4rem;
        line-height: 2.8rem;
        text-decoration: none;
        border-bottom: 1px solid rgba($white, 0.1);
        justify-items: flex-end;
      }
    }

    &.col-2 {
      justify-content: space-between;

      .line {
        align-items: center;
        width: 70%;

        .gauge {
          margin-right: 3rem;
          margin-left: 3.8rem;
        }
      }
    }

    .gauge {
      position: relative;
      flex-grow: 1;
      height: 1rem;
      background: rgba(#ededed, 0.1);
      border-radius: 0.5rem;

      .innerGauge {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background: rgb(21 163 216);
        background: linear-gradient(
          270deg,
          rgb(21 163 216 / 1) 0%,
          rgb(17 114 206 / 1) 100%
        );
        border-radius: 0.5rem;
      }
    }
  }
}

@media screen and (max-width: 1280px) {
  .header-bazar-voice {
    .header-content {
      flex-wrap: wrap;
      padding: 5rem 8rem 9.5rem;
    }

    .col {
      border-right: none;

      &.col-1 {
        flex-direction: row;
        flex-basis: 100%;
        justify-content: space-between;
        margin-bottom: 3rem;
        padding: 0 0 3rem;
        border-right: none;
        border-bottom: 1px solid rgba($white, 0.2);

        .line {
          max-width: 100%;
          border-bottom: none;

          &:first-child {
            padding-right: 2rem;
          }

          &:last-child {
            justify-content: flex-end;
            border-left: 1px solid rgba($white, 0.2);

            .score {
              flex-grow: 0;
            }
          }
        }
      }

      &.col-2 {
        flex-basis: 50%;

        .line {
          width: 100%;
          padding-right: 4.5rem;
        }
      }

      &.col-3 {
        flex-basis: 50%;
        justify-content: flex-end;
        border-left: 1px solid rgba($white, 0.2);

        .category {
          width: 100%;
          max-width: 100%;
          margin-left: 0;
          padding-left: 4.5rem;
        }
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .header-bazar-voice {
    .header-content {
      padding: 5rem 5rem 9.5rem;
    }

    .col {
      &.col-1 {
        .line {
          &:last-child {
            .score {
              margin-left: 2rem;
            }

            .link {
              margin-left: 1.5rem;
            }
          }
        }
      }

      &.col-2 {
        .line {
          padding-right: 3rem;
        }
      }

      &.col-3 {
        .category {
          padding-left: 3rem;
        }
      }
    }
  }
}

@media screen and (max-width: 700px) {
  .header-bazar-voice {
    .header-content {
      padding: 5rem 2.5rem 9.5rem;
    }
  }
}

@media screen and (max-width: 650px) {
  .header-bazar-voice {
    .header-content {
      padding: 3rem 2.5rem 5rem;
    }

    .col {
      &.col-1 {
        flex-wrap: wrap;
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;

        .big {
          font-size: 5rem;
          font-weight: 500;
          line-height: 6.5rem;
        }

        .small,
        .small-bis {
          padding-bottom: 0.8rem;
        }

        .line {
          flex-basis: 100%;
          border-bottom: 1px solid rgba($white, 0.2);

          &:first-child {
            padding-right: 0;
          }

          &:last-child {
            justify-content: flex-start;
            border-left: none;

            .score {
              margin-left: 2rem;
            }

            .link {
              margin-left: 1.5rem;
            }
          }
        }
      }

      &.col-2 {
        flex-basis: 100%;
        margin-top: 3rem;
        padding-bottom: 3rem;
        border-bottom: 1px solid rgba($white, 0.2);

        .line {
          padding-right: 0;

          .gauge {
            margin-right: 2rem;
            margin-left: 2rem;
          }
        }
      }

      &.col-3 {
        flex-basis: 100%;
        margin-top: 3rem;
        border-left: none;

        .category {
          padding-left: 0;
        }
      }
    }
  }
}

@media screen and (max-width: 430px) {
  .header-bazar-voice {
    .col {
      &.col-1 {
        .big {
          font-size: 4rem;
        }

        .line:last-child {
          .link {
            margin-left: auto;
          }
        }
      }
    }
  }
}
</style>
